import React, { useState } from 'react';
import { Typography, Input, Button, Card } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph } = Typography;

const TokenScan = () => {
    const { t } = useTranslation();
    const [ca, setCa] = useState('');
    const [results, setResults] = useState(null);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const API_BASE_URL = 'https://api.kasplex.org/v1/krc20';
    const BLACKHOLE_ADDRESS = 'kaspa:000000000000000000000000000000000000000000000000000000000';

    // 检查代币安全性
    const checkToken = async () => {
        setError('');
        setResults(null);
        setTokenInfo(null);
        setLoading(true);

        try {
            // 1. 获取代币基本信息
            const tokenResponse = await axios.get(`${API_BASE_URL}/token/${ca}`);
            const tokenData = tokenResponse.data.result[0];

            if (!tokenData) {
                setError(t('tokenScan.errors.invalidToken'));
                setLoading(false);
                return;
            }

            // 保存代币基本信息
            setTokenInfo(tokenData);

            // 如果是 Issue 模式，执行安全检查
            if (tokenData.mod === 'issue') {
                // 2. 获取黑名单
                const blacklistResponse = await axios.get(`${API_BASE_URL}/blacklist/${ca}`);
                const blacklist = blacklistResponse.data.result || []; // 确保 blacklist 有默认值

                // 3. 获取操作记录（chown, issue, burn）
                const opResponse = await axios.get(`${API_BASE_URL}/oplist?tick=${ca}`);
                const operations = opResponse.data.result || []; // 确保 operations 有默认值

                // 分析检查项
                const checks = {
                    ownership: {
                        passed: false,
                        message: 'Ownership not renounced',
                    },
                    maxSupply: {
                        passed: tokenData.max !== '0',
                        message: tokenData.max === '0' ? 'Unlimited issuance allowed' : 'Limited issuance',
                    },
                    blacklist: {
                        passed: blacklist.length === 0,
                        message: blacklist.length > 0 ? `Found ${blacklist.length} blacklisted addresses` : 'No blacklist',
                    },
                    preAllocation: {
                        passed: true,
                        message: 'Pre-allocation reasonable',
                    },
                    issueHistory: {
                        passed: true,
                        message: 'No abnormal issuance detected',
                    },
                    burnHistory: {
                        passed: true,
                        message: 'No abnormal burns detected',
                    },
                };

                // 检查权限是否丢弃
                const chownOps = operations.filter(op => op.op === 'chown');
                if (chownOps.length > 0) {
                    const latestChown = chownOps[chownOps.length - 1];
                    if (latestChown.to === BLACKHOLE_ADDRESS) {
                        checks.ownership.passed = true;
                        checks.ownership.message = 'Ownership renounced to blackhole address';
                    } else {
                        checks.ownership.message = `Ownership transferred to ${latestChown.to}`;
                    }
                }

                // 检查预分配比例
                if (tokenData.pre && tokenData.max && tokenData.max !== '0') {
                    const preRatio = (parseFloat(tokenData.pre) / parseFloat(tokenData.max)) * 100;
                    if (preRatio > 30) {
                        checks.preAllocation.passed = false;
                        checks.preAllocation.message = `High pre-allocation: ${preRatio.toFixed(2)}% of max supply`;
                    }
                }

                // 检查增发记录
                const issueOps = operations.filter(op => op.op === 'issue');
                const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
                const recentIssues = issueOps.filter(op => parseInt(op.mtsAdd) > oneMonthAgo);
                let recentIssueTotal = 0;
                recentIssues.forEach(op => {
                    recentIssueTotal += parseFloat(op.amt);
                });
                if (tokenData.max !== '0' && recentIssueTotal > parseFloat(tokenData.max) * 0.1) {
                    checks.issueHistory.passed = false;
                    checks.issueHistory.message = `Abnormal issuance: ${recentIssueTotal} tokens issued in last 30 days`;
                }

                // 检查销毁记录
                const burnOps = operations.filter(op => op.op === 'burn');
                const largeBurns = burnOps.filter(op => parseFloat(op.amt) > parseFloat(tokenData.max || tokenData.minted) * 0.2);
                if (largeBurns.length > 0) {
                    checks.burnHistory.passed = false;
                    checks.burnHistory.message = `Abnormal burns: ${largeBurns.length} large burn operations detected`;
                }

                setResults(checks);
            }
        } catch (err) {
            setError(t('tokenScan.errors.fetchError') + ': ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '80vh',
                padding: '20px',
                backgroundColor: '#fff', // 适配项目风格：白色背景
            }}
        >
            <Title level={2} style={{
                marginTop: '5vh', color: '#00c4b4', textAlign: 'center'
            }}>
                {t('tokenScan.title')}
            </Title>
            <Paragraph style={{ textAlign: 'center', maxWidth: '600px', marginBottom: '20px' }}>
                {t('tokenScan.description')}
            </Paragraph>
            <div style={{ width: '100%', maxWidth: '600px' }}>
                <Card
                    style={{
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        marginBottom: '20px', // 修正重复和错误的 marginBottom
                    }}
                    bodyStyle={{ padding: '20px' }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Input
                            value={ca}
                            onChange={(e) => setCa(e.target.value)}
                            placeholder={t('tokenScan.inputPlaceholder')}
                            style={{ borderRadius: '4px' }}
                            size='large'
                        />
                        <Button
                            type="primary"
                            onClick={checkToken}
                            disabled={loading || !ca}
                            loading={loading}
                            size='large'
                            style={{ backgroundColor: '#00c4b4', borderColor: '#00c4b4', borderRadius: '4px' }}
                        >
                            {loading ? t('tokenScan.loading') : t('tokenScan.scanButton')}
                        </Button>
                    </div>
                    {error && (
                        <Paragraph style={{ color: 'red', marginTop: '10px', textAlign: 'center' }}>
                            {error}
                        </Paragraph>
                    )}
                    {tokenInfo && (
                        <div style={{ marginTop: '20px' }}>
                            <Title level={4} style={{ textAlign: 'center' }}>
                                {tokenInfo.mod === 'issue' ? tokenInfo.name : tokenInfo.tick} Token Details
                            </Title>
                            <Paragraph>
                                <strong>Type:</strong> {tokenInfo.mod.charAt(0).toUpperCase() + tokenInfo.mod.slice(1)}
                            </Paragraph>
                            <Paragraph>
                                <strong>Total Minted:</strong>{' '}
                                {tokenInfo.max !== '0'
                                    ? `${((parseFloat(tokenInfo.minted) / parseFloat(tokenInfo.max)) * 100).toFixed(2)}% (${tokenInfo.minted})`
                                    : tokenInfo.minted}
                            </Paragraph>
                            <Paragraph>
                                <strong>Preminted:</strong>{' '}
                                {tokenInfo.pre && tokenInfo.max !== '0'
                                    ? `${((parseFloat(tokenInfo.pre) / parseFloat(tokenInfo.max)) * 100).toFixed(2)}% (${tokenInfo.pre})`
                                    : '0% (0)'}
                            </Paragraph>
                            <Paragraph>
                                <strong>Burned:</strong>{' '}
                                {tokenInfo.burned && tokenInfo.max !== '0'
                                    ? `${((parseFloat(tokenInfo.burned) / parseFloat(tokenInfo.max)) * 100).toFixed(2)}% (${tokenInfo.burned})`
                                    : '0% (0)'}
                            </Paragraph>
                            <Paragraph>
                                <strong>Status:</strong> {tokenInfo.state.charAt(0).toUpperCase() + tokenInfo.state.slice(1)}
                            </Paragraph>
                            <Paragraph>
                                <strong>Deployed By:</strong> {tokenInfo.to}
                            </Paragraph>
                            <Paragraph>
                                <strong>Holder Count:</strong> {tokenInfo.holderTotal || 'N/A'}
                            </Paragraph>
                        </div>
                    )}
                    {results && tokenInfo.mod === 'issue' && (
                        <div style={{ marginTop: '20px' }}>
                            <Title level={4} style={{ textAlign: 'center' }}>
                                Safety Check Results
                            </Title>
                            <CheckItem
                                label="Ownership Renounced"
                                passed={results.ownership.passed}
                                message={results.ownership.message}
                            />
                            <CheckItem
                                label="Limited Issuance"
                                passed={results.maxSupply.passed}
                                message={results.maxSupply.message}
                            />
                            <CheckItem
                                label="No Blacklist"
                                passed={results.blacklist.passed}
                                message={results.blacklist.message}
                            />
                            <CheckItem
                                label="Reasonable Pre-allocation"
                                passed={results.preAllocation.passed}
                                message={results.preAllocation.message}
                            />
                            <CheckItem
                                label="Normal Issuance History"
                                passed={results.issueHistory.passed}
                                message={results.issueHistory.message}
                            />
                            <CheckItem
                                label="Normal Burn History"
                                passed={results.burnHistory.passed}
                                message={results.burnHistory.message}
                            />
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

// 检查项组件
const CheckItem = ({ label, passed, message }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <span style={{ fontSize: '20px' }}>{passed ? '✅' : '❌'}</span>
        <div>
            <Paragraph style={{ margin: 0, fontWeight: '500' }}>{label}</Paragraph>
            <Paragraph style={{ margin: 0, color: '#666', fontSize: '12px' }}>{message}</Paragraph>
        </div>
    </div>
);

export default TokenScan;